import { AsyncPipe, NgStyle } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgbHighlight, NgbPopover, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  first,
  map,
  merge,
  Observable,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { NewOrganizationProjectFormModel } from 'src/app/core/models/organization/new-organization-project-form.model';
import { SearchProject } from 'src/app/core/models/search/search-project.interface';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { Constants } from 'src/app/core/constants';
import { FileService } from 'src/app/core/services/file.service';

@Component({
  selector: 'app-organizaiton-projects',
  imports: [
    NgbPopover,
    NgbTooltip,
    TranslateModule,
    CoreModule,
    ReactiveFormsModule,
    NgbHighlight,
    RouterLink,
    AsyncPipe,
    NgStyle,
  ],
  templateUrl: './organization-projects.html',
  styleUrl: './organization-projects.scss',
  standalone: true,
  providers: [NewOrganizationProjectFormModel],
})
export class OrganizationProjects implements OnChanges, OnDestroy {
  newOrganizationProjectForm = inject(NewOrganizationProjectFormModel);
  private readonly permissionService = inject(PermissionService);
  private readonly projectService = inject(ProjectService);
  private readonly fileService = inject(FileService);

  @Input({ required: true })
  public companyId = '';

  @Input({ required: true })
  public organizationId = '';

  @Output() public createProjectRequested = new EventEmitter<void>();

  public readonly notFound = Constants.NotFound;

  public isLoading = false;

  public readonly canCreateProjects$ = this.permissionService.userPermissions$.pipe(
    map(permissions =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Project,
        UserCompanyPermissions.Create,
      ),
    ),
  );

  public projectTitleFilter: (project: SearchProject, term: string) => boolean = (project, term) =>
    project.title.toLowerCase().includes(term.toLowerCase());

  public projectFormatter: (project: SearchProject | null | undefined) => string = project =>
    project?.title ?? '';

  public projectCompareFn: (project: SearchProject, value: SearchProject) => boolean = (
    project,
    value,
  ) => project.title === value.title;

  public showAll$ = new BehaviorSubject<boolean>(false);

  private readonly companyId$ = new BehaviorSubject<string | null>(null);
  private readonly organizationId$ = new BehaviorSubject<string | null>(null);

  public companyProjects$ = this.companyId$.pipe(
    filter((companyId): companyId is string => Boolean(companyId)),
    switchMap(companyId => this.projectService.getProjectsForSearch(companyId)),
  );

  public organizationProjects$ = combineLatest([this.companyId$, this.organizationId$]).pipe(
    filter((ids): ids is [string, string] => Boolean(ids[0]) && Boolean(ids[1])),
    switchMap(([companyId, organizationId]) =>
      this.projectService.getOrganizationProjects(companyId, organizationId),
    ),
  );

  public readonly displayedProjects$ = combineLatest([
    this.organizationProjects$,
    this.showAll$,
  ]).pipe(map(([contacts, showAll]) => (showAll ? contacts : contacts.slice(0, 3))));

  private unsubscribe$ = new Subject<void>();

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['organizationId'] || !this.companyId || !this.organizationId) {
      return;
    }

    this.projectService.setProjectsUrl(this.companyId);
    this.projectService.setCompanyId(this.companyId);

    this.companyId$.next(this.companyId);
    this.organizationId$.next(this.organizationId);

    this.reloadProjects();
  }

  public addProject(popover: NgbPopover): void {
    const projectId = this.newOrganizationProjectForm.form.get('newProjectId')?.value;

    if (!projectId) {
      return;
    }

    this.isLoading = true;

    this.projectService
      .patchProject(projectId, 'organizationId', this.organizationId)
      .pipe(
        first(),
        tap(() => {
          this.projectService.invalidateOrganizationProjects(this.companyId, this.organizationId);
          this.projectService.invalidateProjectsForSearch(this.companyId);

          this.reloadProjects();
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();

    popover.close();
  }

  public openCreateProjectModal(popover?: NgbPopover): void {
    popover?.close();
    this.createProjectRequested.emit();
  }

  public toggleProjects(): void {
    this.showAll$.next(!this.showAll$.value);
  }

  private reloadProjects(): void {
    const loads: Observable<unknown>[] = [];

    if (!this.projectService.hasOrganizationProjects(this.companyId, this.organizationId)) {
      loads.push(this.projectService.loadOrganizationProjects(this.companyId, this.organizationId));
    }

    if (!this.projectService.hasProjectsForSearch(this.companyId)) {
      loads.push(this.projectService.loadProjectsForSearch(this.companyId, this.organizationId));
    }

    if (!loads.length) {
      return;
    }

    this.isLoading = true;

    merge(...loads)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }
}
